<template>
    <div
        :style="meta.background_image ? `background-image: url(${meta.background_image});` : ''"
        class="bg-no-repeat bg-center bg-cover min-h-full"
    >
        <div class="bg-white shadow tw-glass">
            <div class="px-4 sm:px-6 lg:max-w-6xl lg:mr-auto lg:px-8">
                <div class="py-6 md:flex md:items-center md:justify-between lg:border-t lg:border-gray-200">
                    <div class="flex-1 min-w-0">
                        <!-- Profile -->
                        <div class="flex items-top md:items-center space-x-2">
                            <ZoomableImage :src="student.avatar_or_profile_photo">
                                <img
                                    class="hidden h-16 w-16 rounded-full sm:block"
                                    :src="student.avatar_or_profile_photo"
                                    alt="Avatar"
                                />
                            </ZoomableImage>
                            <div>
                                <div class="flex items-center">
                                    <img
                                        class="h-16 w-16 rounded-full sm:hidden"
                                        :src="student.avatar_or_profile_photo"
                                        alt="Avatar"
                                    />
                                    <h1
                                        class="ml-3 text-2xl font-bold leading-7 text-gray-900 sm:leading-9 sm:truncate"
                                    >
                                        <!--                                    {{  trans_choice('messages.good-morning', 1, ['name' => auth()->user()->first_name]) }}-->
                                        <template v-if="student.show_faint_icon"> ☠️ </template>
                                        <template v-if="student.is_birthday">
                                            🎂
                                            {{
                                                $t('messages.happy_birthday_message', {
                                                    name: student.name_or_nickname,
                                                })
                                            }}
                                        </template>
                                        <template v-else>
                                            {{
                                                $t('messages.good_morning', {
                                                    name: student.name_or_nickname,
                                                })
                                            }}
                                            <span v-if="student.rank"> ({{ student.rank }}) </span>
                                        </template>
                                    </h1>
                                </div>
                                <dl class="mt-6 flex flex-col sm:ml-3 sm:mt-1 sm:flex-row sm:flex-wrap">
                                    <dt class="sr-only">Role</dt>
                                    <dd class="flex items-center text-sm text-gray-500 font-medium capitalize sm:mr-6">
                                        <svg
                                            class="shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                            aria-hidden="true"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                                                clip-rule="evenodd"
                                            ></path>
                                        </svg>
                                        <span class="mr-1.5">{{ $tc('messages.student', 1) }}</span>
                                        <span v-if="student.character_name">({{ student.character_name }})</span>
                                    </dd>
                                </dl>
                            </div>
                            <div v-if="student.pet" class="h-16 w-16">
                                <img :src="student.pet.image_path" alt="" class="object-contain" />
                            </div>
                            <img
                                v-if="student.group_image_path"
                                class="w-12 h-12 object-contain"
                                :src="student.group_image_path"
                                alt=""
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="bg-indigo-800 p-2">
            <div
                class="max-w-7xl mx-auto md:px-4 sm:px-6 md:px-8 flex space-x-2 overflow-x-auto scrollbar-none items-center space-x-4 lg:space-x-6"
            >
                <div class="flex-none" v-for="badge in badges" :title="badge.name" :key="badge.id">
                    <InertiaLink :href="meta.links.badges_index">
                        <img
                            class="w-16 h-16 rounded-full lg:w-20 lg:h-20"
                            :class="badge.obtained ? 'bg-indigo-400' : 'bg-indigo-600 opacity-25'"
                            :src="badge.image_path"
                            alt=""
                        />
                    </InertiaLink>
                </div>
            </div>
        </div>

        <GamificationEventsDashboardBanner :gamificationEvents="gamificationEvents" />
        <DuelsDashboardBanner :duels="duels" />

        <div class="mt-8" v-if="announcements.length > 0">
            <div class="mx-auto px-4 sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow rounded-lg md:col-span-6">
                    <div class="px-4 py-5 sm:p-6">
                        <h3 class="text-lg leading-6 font-medium text-gray-900">
                            {{ $tc('messages.announcements', 2) }}
                        </h3>
                        <div class="mt-5" v-for="announcement in announcements" :key="announcement.id">
                            <div class="rounded-md bg-yellow-50 px-6 py-5 sm:flex sm:items-start sm:justify-between">
                                <div class="sm:flex sm:items-start">
                                    <svg
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        class="information-circle shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-500 transition ease-in-out duration-150"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                            clip-rule="evenodd"
                                        ></path>
                                    </svg>
                                    <div class="mt-3 sm:mt-0 sm:ml-4">
                                        <div class="text-lg leading-5 font-medium text-gray-900">
                                            {{ announcement.title }}
                                        </div>
                                        <div class="mt-1 text-sm leading-5 text-gray-600 sm:flex sm:items-center">
                                            <div>
                                                <p v-html="announcement.body" class="prose prose-sm" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!--        Widgets section-->
        <DashboardStudentWidgets :widgets="widgets" />
        <DashboardSummary v-if="showDashboardSummary" :score-card-summary="student.dashboard_summary" />
    </div>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import DashboardStudentWidgets from '@/components/DashboardStudentWidgets.vue';
import GamificationEventsDashboardBanner from '@/components/GamificationEventsDashboardBanner.vue';
import ZoomableImage from '@/components/ZoomableImage.vue';
import DashboardSummary from '@/Pages/Student/Dashboard/Partials/DashboardSummary.vue';
import DuelsDashboardBanner from '@/Pages/Student/Dashboard/Partials/DuelsDashboardBanner.vue';

export default {
    layout: Layout,
    name: 'Show',
    components: {
        DuelsDashboardBanner,
        DashboardSummary,
        ZoomableImage,
        DashboardStudentWidgets,
        GamificationEventsDashboardBanner,
    },
    props: ['student', 'widgets', 'gamificationEvents', 'badges', 'duels', 'announcements', 'meta'],
    data() {
        return {
            showDashboardSummary: false,
        };
    },
    mounted() {
        if (this.student.dashboard_summary.shouldShow) {
            this.showDashboardSummary = true;
        }
    },
};
</script>
<style scoped>
p.prose {
    max-width: 100%;
}
.tw-glass {
    @apply bg-clip-padding backdrop-filter backdrop-blur-xl bg-opacity-60 border border-gray-200;
}
</style>
